/**
 * Merchant-products hooks (list, CRUD, featured toggle).
 *
 * Wired to the **merchant-scoped** controller
 * (`/api/v1/merchants/{merchantId}/products`), which authorises any merchant
 * member *or* an admin (admin bypasses membership). This is deliberate: the
 * older `/api/v1/admin/merchants/{id}/products` endpoints are
 * `@PreAuthorize("hasRole('ADMIN')")`, so a logged-in merchant hits 403 there.
 *
 * The generated OpenAPI bindings (`api.d.ts`) are **stale** for these paths —
 * they still expose a free-text `category` string and omit `merchantCategoryId`
 * plus the prep-time / stock / tags / allergens / option-group fields. Until a
 * regeneration lands (tracked as audit §3.1) we model the real contract with
 * the local interfaces below and call the endpoints through `as never` casts
 * (same pattern as `categories.ts`). The backend DTOs are the source of truth:
 *   - CreateProductRequest: name, description?, merchantCategoryId (required),
 *     price, imageUrl?, preparationTimeMinutes?, stockQuantity?, tags?,
 *     allergenIds?
 *   - ProductResponse: …, merchantCategoryId, …, allergens[], optionGroups[],
 *     computed{available,minPrice,maxPrice}
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';
import type { components } from '@favelafood/shared/api/generated/api';
import type { AllergenResponse } from '@/features/allergens/hooks';
import { merchantOpsKeys, unwrap } from './keys';
import type { OptionGroupResponse } from './optionGroups';

/** Derived/read-only fields the backend computes on `ProductResponse`. */
export interface ProductComputedFields {
  available?: boolean;
  minPrice?: number;
  maxPrice?: number;
}

/**
 * The real `ProductResponse` contract (mirrors the backend DTO). Hand-modelled
 * because the generated schema is stale — see file header.
 */
export interface ProductResponse {
  id?: string;
  merchantId?: string;
  name?: string;
  description?: string;
  merchantCategoryId?: string;
  price?: number;
  imageUrl?: string;
  isActive?: boolean;
  isFeatured?: boolean;
  preparationTimeMinutes?: number | null;
  stockQuantity?: number | null;
  tags?: string[];
  allergens?: AllergenResponse[];
  optionGroups?: OptionGroupResponse[];
  computed?: ProductComputedFields;
  createdAt?: string;
  updatedAt?: string;
}

export type ProductPage = Omit<
  components['schemas']['PageResponseProductResponse'],
  'content'
> & {
  content?: ProductResponse[];
};

/** Effective page size — the server clamps `?size` to 100 (FF-SEC-016). */
const PAGE_SIZE = 100;
/** Page-walk ceiling: 50 × 100 = 5.000, the same ceiling as the .xlsx import parser. */
const MAX_PAGES = 50;

/**
 * Create payload — `merchantCategoryId` is `@NotNull` on the backend.
 * Optional fields allow explicit `undefined` (dropped by JSON serialization)
 * so callers can build them unconditionally under `exactOptionalPropertyTypes`.
 */
export interface CreateProductRequest {
  name: string;
  description?: string | undefined;
  merchantCategoryId: string;
  price: number;
  imageUrl?: string | undefined;
  preparationTimeMinutes?: number | undefined;
  stockQuantity?: number | undefined;
  tags?: string[] | undefined;
  allergenIds?: string[] | undefined;
}

/** Update payload — every field optional (PATCH-like PUT semantics). */
export interface UpdateProductRequest {
  name?: string | undefined;
  description?: string | undefined;
  merchantCategoryId?: string | undefined;
  price?: number | undefined;
  imageUrl?: string | undefined;
  isActive?: boolean | undefined;
  isFeatured?: boolean | undefined;
  preparationTimeMinutes?: number | undefined;
  stockQuantity?: number | undefined;
  tags?: string[] | undefined;
  allergenIds?: string[] | undefined;
}

export function useMerchantProducts(
  merchantId: string | null,
  includeInactive: boolean,
): UseQueryResult<ProductPage, Error> {
  return useQuery<ProductPage, Error>({
    queryKey: merchantOpsKeys.products(merchantId ?? 'none', includeInactive),
    enabled: merchantId !== null,
    queryFn: async () => {
      if (!merchantId) throw new Error('Selecione um estabelecimento');
      // `includeInactive` is honoured server-side by MerchantProductController.
      //
      // The server silently clamps ?size to 100 (spring.data.web.pageable.max-page-size,
      // FF-SEC-016), so a single page loses every product past the 100th — the panel then
      // reports a wrong count and the .xlsx import rebuilds its create-vs-update index from a
      // truncated catalogue, turning the rest into duplicates. Walk the pages instead.
      //
      // `sort` is required: the catalogue query has no ORDER BY, and LIMIT/OFFSET without one
      // may repeat and skip rows across pages in Postgres.
      const content: ProductResponse[] = [];
      let last = {} as ProductPage;
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const { data, error } = await api.GET(
          '/api/v1/merchants/{merchantId}/products' as never,
          {
            params: {
              path: { merchantId },
              query: { page, size: PAGE_SIZE, sort: 'id,asc', includeInactive },
            },
          } as never,
        );
        last = unwrap(data as ProductPage | undefined, error);
        content.push(...(last.content ?? []));
        if (page + 1 >= (last.totalPages ?? 1)) break;
      }
      // Collapse the walked pages into a single logical page so callers keep reading `.content`.
      return { ...last, content, page: 0, size: content.length, first: true, last: true };
    },
    // Up to 50 GETs for a large catalogue — cache it so switching tabs doesn't re-walk.
    // Product mutations below invalidate this key, so writes still show up immediately.
    staleTime: 5 * 60_000,
  });
}

interface CreateProductVars {
  merchantId: string;
  body: CreateProductRequest;
}
interface UpdateProductVars {
  merchantId: string;
  productId: string;
  body: UpdateProductRequest;
}
interface DeleteProductVars {
  merchantId: string;
  productId: string;
}
interface ToggleFeaturedVars {
  merchantId: string;
  productId: string;
}

/**
 * `silent` suppresses the cache invalidation in `onSuccess`.
 *
 * For loops: the product list is read with a page-walk of up to 50 requests, and the import
 * dialog keeps that observer mounted while it runs. Invalidating per row would multiply the
 * walk by the row count; the caller invalidates once when the loop is done.
 */
export interface MutationScopeOptions {
  silent?: boolean;
}

export function useCreateProduct(
  opts: MutationScopeOptions = {},
): UseMutationResult<ProductResponse, Error, CreateProductVars> {
  const qc = useQueryClient();
  return useMutation<ProductResponse, Error, CreateProductVars>({
    mutationFn: async ({ merchantId, body }) => {
      const { data, error } = await api.POST(
        '/api/v1/merchants/{merchantId}/products' as never,
        { params: { path: { merchantId } }, body } as never,
      );
      return unwrap(data as ProductResponse | undefined, error);
    },
    onSuccess: () => {
      if (opts.silent) return;
      void qc.invalidateQueries({ queryKey: merchantOpsKeys.all });
    },
  });
}

export function useUpdateProduct(
  opts: MutationScopeOptions = {},
): UseMutationResult<ProductResponse, Error, UpdateProductVars> {
  const qc = useQueryClient();
  return useMutation<ProductResponse, Error, UpdateProductVars>({
    mutationFn: async ({ merchantId, productId, body }) => {
      const { data, error } = await api.PUT(
        '/api/v1/merchants/{merchantId}/products/{id}' as never,
        { params: { path: { merchantId, id: productId } }, body } as never,
      );
      return unwrap(data as ProductResponse | undefined, error);
    },
    onSuccess: () => {
      if (opts.silent) return;
      void qc.invalidateQueries({ queryKey: merchantOpsKeys.all });
    },
  });
}

export function useDeleteProduct(): UseMutationResult<void, Error, DeleteProductVars> {
  const qc = useQueryClient();
  return useMutation<void, Error, DeleteProductVars>({
    mutationFn: async ({ merchantId, productId }) => {
      const { error } = await api.DELETE(
        '/api/v1/merchants/{merchantId}/products/{id}' as never,
        { params: { path: { merchantId, id: productId } } } as never,
      );
      if (error) throw error as Error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: merchantOpsKeys.all });
    },
  });
}

export function useToggleFeatured(): UseMutationResult<ProductResponse, Error, ToggleFeaturedVars> {
  const qc = useQueryClient();
  return useMutation<ProductResponse, Error, ToggleFeaturedVars>({
    // The featured endpoint is a toggle with NO request body — it flips the
    // current state server-side and returns the updated product. Trust the
    // response; never assume the new value client-side.
    mutationFn: async ({ merchantId, productId }) => {
      const { data, error } = await api.PATCH(
        '/api/v1/merchants/{merchantId}/products/{id}/featured' as never,
        { params: { path: { merchantId, id: productId } } } as never,
      );
      return unwrap(data as ProductResponse | undefined, error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: merchantOpsKeys.all });
    },
  });
}
